import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cx, focusRing } from './cx';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import { Spinner } from './Button';
import type { ButtonVariant } from './Button';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconName;
  /** Required: an icon-only control has no text for a screen reader to read. */
  label: string;
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  loading?: boolean;
}

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-forest text-[rgb(var(--ct-on-brand-rgb))] hover:bg-forest-deep active:bg-forest-deep border border-transparent',
  // A hairline, as the secondary Button is: an ink box reads as loud as a
  // primary.
  secondary: 'bg-paper-card text-ink border border-line-strong hover:bg-forest-whisper active:bg-forest-whisper',
  ghost: 'bg-transparent text-ink-2 border border-transparent hover:bg-forest-whisper hover:text-ink active:bg-forest-whisper',
  danger: 'bg-transparent text-danger border border-transparent hover:bg-danger-soft active:bg-danger-soft',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, variant = 'ghost', size = 'md', loading, className, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-label={label}
      title={label}
      className={cx(
        // The visible box may look small, but the hit area never drops below
        // 44px: these sit in register rows where a mis-tap deletes something.
        'inline-flex items-center justify-center rounded-ct-sm min-h-tap min-w-tap shadow-none',
        'transition-colors duration-state ease-ct disabled:opacity-50 disabled:cursor-not-allowed',
        focusRing,
        variants[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 16 : 18} /> : <Icon name={icon} size={size === 'sm' ? 18 : 20} />}
    </button>
  );
});
